/**
 * Lost & Found — colored type tile (lost/found), title, where, reporter,
 * when. Wired: GET /api/discussions/lost-found. Port of LostFoundScreen in
 * design/source/screens-vendor-issue.jsx.
 */
import React from 'react';
import { View } from 'react-native';

import { radius, space } from '@bero/tokens';
import { Icon } from '@/components/gate/icons';
import { goBack } from '@/features/community/bits';
import { fetchLostFound, useFetch, type LostFoundItem } from '@/features/community/lib';
import { useT } from '@/i18n';
import { useTheme } from '@/theme';
import { AppText, EmptyState, Hairline, Pill, Screen, Skeleton, TopBar } from '@/ui';

export default function LostFoundScreen() {
  const { colors } = useTheme();
  const { t } = useT();
  const { data, loading, error, refetch } = useFetch(fetchLostFound, []);
  const list = data ?? [];

  return (
    <View style={{ flex: 1, backgroundColor: colors.canvas0 }}>
      <TopBar title={t('ml_lost_found')} onBack={goBack} />
      <Screen padded style={{ paddingTop: space.s4 }}>
        {loading ? (
          <View style={{ gap: space.s3 }}>
            {[0, 1, 2].map((i) => <Skeleton key={i} h={80} />)}
          </View>
        ) : error ? (
          <EmptyState
            title={t('lf_err_title')}
            body={t('lf_err_body')}
            cta={t('retry')}
            onCta={() => void refetch()}
          />
        ) : list.length === 0 ? (
          <EmptyState
            icon={<Icon name="package" size={40} color={colors.textTertiary} />}
            title={t('lf_empty_title')}
            body={t('lf_empty_body')}
          />
        ) : (
          <View>
            {list.map((it, i) => (
              <View key={it.id}>
                <LostFoundRow item={it} />
                {i < list.length - 1 ? <Hairline /> : null}
              </View>
            ))}
          </View>
        )}
      </Screen>
    </View>
  );
}

function LostFoundRow({ item }: { item: LostFoundItem }) {
  const { colors } = useTheme();
  const { t } = useT();
  const found = item.type === 'FOUND';
  return (
    <View style={{ flexDirection: 'row', gap: space.s3, paddingVertical: space.s4 }}>
      <View style={{
        width: 44, height: 44, borderRadius: radius.md,
        backgroundColor: found ? colors.success50 : colors.warning50,
        alignItems: 'center', justifyContent: 'center',
      }}>
        <Icon name={found ? 'check' : 'alert-triangle'} size={18} color={found ? colors.success500 : colors.warning500} />
      </View>
      <View style={{ flex: 1, minWidth: 0 }}>
        <View style={{ flexDirection: 'row', alignItems: 'center' }}>
          <Pill status={found ? 'resolved' : 'progress'} label={t(found ? 'pill_found' : 'pill_lost')} />
          {item.when ? (
            <AppText variant="bodySm" tone="tertiary" style={{ marginLeft: 'auto' }}>{item.when}</AppText>
          ) : null}
        </View>
        <AppText variant="bodyMd" weight="500" style={{ marginTop: 6 }}>{item.title}</AppText>
        <AppText variant="bodySm" tone="tertiary" style={{ marginTop: 2 }}>
          {item.where}{item.where && item.by ? ' · ' : ''}{item.by}
        </AppText>
      </View>
    </View>
  );
}
